import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addSupportedDocumentAPI,
  getSupportedDocumentsListAPI,
  getSupportedDocumentByIdAPI,
  updateSupportedDocumentAPI,
  archiveSupportedDocumentAPI,
} from "./supportedDocumentsAPI";

export const addSupportedDocument = createAsyncThunk(
  "supportedDocuments/add",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await addSupportedDocumentAPI(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add document");
    }
  }
);

export const getSupportedDocumentsList = createAsyncThunk(
  "supportedDocuments/listing",
  async (params, { rejectWithValue }) => {
    try {
      // Normalize params to ensure API gets page, limit, search, access_group_id, and archived
      const {
        page = 1,
        limit = 10,
        search = "",
        access_group_id = [],
        archived // Extract archived
      } = params || {};

      // Ensure access_group_id is always an array
      const normalizedAccessGroupId = Array.isArray(access_group_id)
        ? access_group_id
        : access_group_id ? [access_group_id] : [];

      const response = await getSupportedDocumentsListAPI({
        page,
        limit,
        search,
        access_group_id: normalizedAccessGroupId,
        archived // Pass archived to API function
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch document list");
    }
  }
);

export const getSupportedDocumentById = createAsyncThunk(
  "supportedDocuments/detail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getSupportedDocumentByIdAPI(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch document details");
    }
  }
);

export const updateSupportedDocument = createAsyncThunk(
  "supportedDocuments/edit",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await updateSupportedDocumentAPI({ id, formData });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update document");
    }
  }
);

export const archiveSupportedDocument = createAsyncThunk(
  "supportedDocuments/archive",
  async (payload, { rejectWithValue }) => {
    try {
      // Logic to handle if payload is just an ID or an object {id, is_archive}
      const id = typeof payload === 'object' ? payload.id : payload;
      const is_archive = typeof payload === 'object' && payload.is_archive !== undefined ? payload.is_archive : true;

      const response = await archiveSupportedDocumentAPI({ id, is_archive });
      
      // Return id and status so we can filter the list in the reducer
      return { ...response, archivedId: id, isArchiveRequest: is_archive };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive document");
    }
  }
);

const initialState = {
  addSupportedDocument: { data: null, loading: false, error: null },
  updateSupportedDocument: { data: null, loading: false, error: null },
  getSupportedDocumentsList: { data: [], total: 0, loading: false, error: null },
  getSupportedDocumentById: { data: null, loading: false, error: null },
  archiveSupportedDocument: { data: null, loading: false, error: null },
};

const supportedDocumentsSlice = createSlice({
  name: "supportedDocuments",
  initialState,
  reducers: {
    clearAddSupportedDocument: (state) => {
      state.addSupportedDocument.data = null;
      state.addSupportedDocument.error = null;
    },
    clearUpdateSupportedDocument: (state) => {
      state.updateSupportedDocument.data = null;
      state.updateSupportedDocument.error = null;
    },
    clearGetSupportedDocumentById: (state) => {
      state.getSupportedDocumentById.data = null;
      state.getSupportedDocumentById.error = null;
    },
    clearArchiveSupportedDocument: (state) => {
      state.archiveSupportedDocument.data = null;
      state.archiveSupportedDocument.error = null;
    },
    clearSupportedDocumentsList: (state) => {
      state.getSupportedDocumentsList.data = [];
      state.getSupportedDocumentsList.total = 0;
      state.getSupportedDocumentsList.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add
      .addCase(addSupportedDocument.pending, (state) => {
        state.addSupportedDocument.loading = true;
        state.addSupportedDocument.error = null;
      })
      .addCase(addSupportedDocument.fulfilled, (state, action) => {
        state.addSupportedDocument.loading = false;
        state.addSupportedDocument.data = action.payload;
      })
      .addCase(addSupportedDocument.rejected, (state, action) => {
        state.addSupportedDocument.loading = false;
        state.addSupportedDocument.error = action.payload;
      })

      // List
      .addCase(getSupportedDocumentsList.pending, (state) => {
        state.getSupportedDocumentsList.loading = true;
        state.getSupportedDocumentsList.error = null;
      })
      .addCase(getSupportedDocumentsList.fulfilled, (state, action) => {
        state.getSupportedDocumentsList.loading = false;
        state.getSupportedDocumentsList.data = action.payload?.data || [];
        state.getSupportedDocumentsList.total =
          action.payload?.total ||
          action.payload?.count ||
          (action.payload?.data ? action.payload.data.length : 0);
      })
      .addCase(getSupportedDocumentsList.rejected, (state, action) => {
        state.getSupportedDocumentsList.loading = false;
        state.getSupportedDocumentsList.error = action.payload;
      })

      // Detail
      .addCase(getSupportedDocumentById.pending, (state) => {
        state.getSupportedDocumentById.loading = true;
        state.getSupportedDocumentById.error = null;
      })
      .addCase(getSupportedDocumentById.fulfilled, (state, action) => {
        state.getSupportedDocumentById.loading = false;
        state.getSupportedDocumentById.data = action.payload?.data || action.payload;
      })
      .addCase(getSupportedDocumentById.rejected, (state, action) => {
        state.getSupportedDocumentById.loading = false;
        state.getSupportedDocumentById.error = action.payload;
      })

      // Update
      .addCase(updateSupportedDocument.pending, (state) => {
        state.updateSupportedDocument.loading = true;
        state.updateSupportedDocument.error = null;
      })
      .addCase(updateSupportedDocument.fulfilled, (state, action) => {
        state.updateSupportedDocument.loading = false;
        state.updateSupportedDocument.data = action.payload;
      })
      .addCase(updateSupportedDocument.rejected, (state, action) => {
        state.updateSupportedDocument.loading = false;
        state.updateSupportedDocument.error = action.payload;
      })

      // Archive/Delete
      .addCase(archiveSupportedDocument.pending, (state) => {
        state.archiveSupportedDocument.loading = true;
        state.archiveSupportedDocument.error = null;
      })
      .addCase(archiveSupportedDocument.fulfilled, (state, action) => {
        state.archiveSupportedDocument.loading = false;
        state.archiveSupportedDocument.data = action.payload;
        
        // Remove the archived document from the list if it was an archive action (is_archive = true)
        if (action.payload?.isArchiveRequest) {
          state.getSupportedDocumentsList.data = state.getSupportedDocumentsList.data.filter(
            (doc) => doc.id !== action.payload.archivedId && doc.supported_doc_id !== action.payload.archivedId
          );
        }
      })
      .addCase(archiveSupportedDocument.rejected, (state, action) => {
        state.archiveSupportedDocument.loading = false;
        state.archiveSupportedDocument.error = action.payload;
      });
  },
});

export const {
  clearAddSupportedDocument,
  clearUpdateSupportedDocument,
  clearGetSupportedDocumentById,
  clearArchiveSupportedDocument,
  clearSupportedDocumentsList,
} = supportedDocumentsSlice.actions;

export default supportedDocumentsSlice.reducer;